import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider.vue';
import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import moment from 'moment';
import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';
import { PosibleRecreditsResponse, Subscription } from '@subscriptions/interfaces/subscription';
import { Contact } from '@contacts/interfaces/contact';
import InputGroup from '@shared/components/forms/inputs/input-group/input-group.vue';
import AdjustCreditsModal from '@subscriptions/components/modals/adjust-credits-modal/adjust-credits-modal.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSlidersH } from '@fortawesome/free-solid-svg-icons';

library.add(faSlidersH);

@Component({
  name: 'bpc-subscription-form',
  components: {
    'bpc-std-form': StdForm,
    'bpc-entity-select': EntitySelect,
    'bpc-input-group': InputGroup,
    'bpc-adjust-credits-modal': AdjustCreditsModal,
  },
})
export default class SubscriptionForm extends Vue {
  $refs: {
    // resourceForm: InstanceType<typeof ResourceForm> & { saveEntity(): void };
    stdForm: InstanceType<typeof StdForm> & {
      saveEntity(): void;
      formModel: any;
      onDeleteMediaFile(): void;
      searchEntity(): void;
      extraData: string | any;
      formProvider: EntityFormProvider;
    };
  };

  @Prop([String, Number]) readonly entityId: string | number;
  @Prop({ type: String, default: 'default' }) formConfigName: string;
  @Prop({ type: Boolean, default: false }) disabled: string;
  @Prop([String, Object, Array]) readonly extraData: string | AnyEntity;

  public entity: string = 'subscription';
  public permissionEntityNameToUse: string = 'subscriptions';
  public formModel: Subscription = null;
  public mandate = null;
  public mandateKey = 1;
  public errorsObject = {};
  public posibleRecredits: PosibleRecreditsResponse[] = [];
  public showPosibleRecreditWarning: boolean = false;

  public currentSubscription: Subscription = null;
  public showAdjustCreditModal: boolean = false;

  get contactChangeDetectorStr(): string {
    return this.selectedContactIds.join(',');
  }

  get selectedContactIds() {
    if (!this.formModel) {
      return [];
    }

    if (!this.formModel.contact_id) {
      return [];
    }

    return Array.isArray(this.formModel.contact_id)
      ? this.formModel.contact_id.map((contactOrId: number | Contact) => {
          if (typeof contactOrId === 'object') {
            if (contactOrId) {
              return Number(contactOrId?.id);
            }
          } else {
            return Number(contactOrId);
          }
        })
      : [this.formModel.contact_id];
  }

  get isPeriodicPaymentEnabled(): boolean {
    // NOTE depending if it is creation or edition the field is a bit different
    return (
      this.formModel?.subscriptionCategory?.enable_periodic_payment ||
      this.formModel?.subscription_category?.enable_periodic_payment
    );
  }
  get mandateRefreshKey() {
    return `mandateRefreshKey${this.mandateKey}`;
  }
  get sepaMandateConfig() {
    return {
      name: 'mandate',
      label: 'global.subscription.mandate.label',
      fieldAttributes: { autocomplete: 'off' },
      // showLabel: true,
      labelAttributes: {
        for: 'global-subscription__mandate',
        cols: 12,
        'sr-only': false,
        align: 'left',
        size: 'md',
      },
      wrapperAttributes: { class: { 'inline-field col-md-12': true } },
      selectLabel: 'name',
      selectEntity: 'mandate',
      selectMultiple: false,
      withNew: true,
      formConfigName: 'default-from-subscription',
    };
  }

  get sepaDirectDebitActivated() {
    return this.$store.getters['centerSettings/centerSettings'].activate_sepa_direct_debit;
  }

  get showMandateField() {
    return this.sepaDirectDebitActivated && this.isPeriodicPaymentEnabled;
  }

  get mandateValidationErrors() {
    return this.errorsObject?.['mandate_id'];
  }

  get totalCreditsLabel() {
    let type = 'subscription';
    if (this.formModel?.subscriptionCategory?.type_value) {
      type = this.formModel.subscriptionCategory.type_value;
    } else if (this.formModel?.subscriptionCategory?.type) {
      type = this.formModel.subscriptionCategory.type;
    }
    switch (type) {
      case 'time_based':
        return 'global.subscription.total_minutes.label';
      default:
        return 'global.subscription.total_credits.label';
    }
  }

  get remainingCreditsLabel() {
    let type = 'subscription';
    if (this.formModel?.subscriptionCategory?.type_value) {
      type = this.formModel.subscriptionCategory.type_value;
    } else if (this.formModel?.subscriptionCategory?.type) {
      type = this.formModel.subscriptionCategory.type;
    }
    switch (type) {
      case 'time_based':
        return 'global.subscription.remaining_minutes.label';
      default:
        return 'global.subscription.remaining_credits.label';
    }
  }
  get adjustCreditButtonLabel() {
    let type = 'subscription';
    if (this.formModel?.subscriptionCategory?.type_value) {
      type = this.formModel.subscriptionCategory.type_value;
    } else if (this.formModel?.subscriptionCategory?.type) {
      type = this.formModel.subscriptionCategory.type;
    }
    switch (type) {
      case 'time_based':
        return 'global.adjust_minutes.button.label';
      default:
        return 'global.adjust_credits.button.label';
    }
  }

  get totalCreditsFieldConfig() {
    return {
      fieldPath: 'number_credits',
      fieldReadPath: 'number_credits',
      id: 'global-subscription-category__number-credits',
      name: 'number_credits',
      label: 'global.subscription_category.number_credits.label',
      rules: ['nullable', 'numeric'],
      fieldAttributes: {
        autocomplete: 'off',
        type: 'number',
        step: '1',
      },
      fieldClass: 'global-subscription-category__number-credits--input',
      labelClass: 'global-subscription-category__number-credits--input-label',
      labelAttributes: {
        for: 'global-subscription-category__number-credits',
        cols: 12,
        'sr-only': false,
        align: 'left',
        size: 'md',
      },
      wrapperAttributes: {
        class: {
          'inline-field col-md-4': true,
        },
      },
    };
  }

  get remainingCreditsFieldConfig() {
    return {
      fieldPath: 'remaining_credits',
      errorFieldPath: 'remaining_credits',
      fieldReadPath: 'remaining_credits',
      name: 'remaining_credits',
      label: 'global.subscription.remaining_credits.label',
      id: 'global-subscription__remaining-credits',
      rules: ['nullable'],
      fieldAttributes: {
        autocomplete: 'off',
        type: 'number',
        step: '1',
      },
      fieldClass: 'global-subscription__remaining-credits--input',
      labelAttributes: {
        for: 'global-subscription__remaining-credits',
        cols: 12,
        'sr-only': false,
        align: 'left',
        size: 'md',
      },

      wrapperAttributes: {
        class: {
          'inline-field col-md-4': true,
        },
      },
    };
  }

  setExtraFieldsBeforeSave(entityCopy) {
    this.$set(entityCopy, 'mandate_id', this.mandate?.id || null);
    delete entityCopy.mandate;
    return entityCopy;
  }

  onValidationErrorsChanged(errorsObject) {
    this.errorsObject = errorsObject;
    this.$emit('validation-errors-changed', errorsObject);
  }

  // created() {}
  mounted() {
    this.$root.$on('setCategoryFeatures', event => {
      this.setSubscriptionDates(event);
      this.setSubscriptionCredits(event);
    });
    this.$root.$on('updateSubscriptionPrice', event => {
      this.setSubscriptionPrice();
    });
  }

  destroyed() {
    this.$root.$off('setCategoryFeatures');
    this.$root.$off('updateSubscriptionPrice');
  }

  onFormSaved(e) {
    this.$emit('entity-saved', e);
  }
  onSavingEntity(event) {
    this.$emit('saving-entity', event);
  }
  deleteMediaFile(mediaId) {
    this.$emit('delete-media-file', { mediaId: mediaId, entityId: this.entityId });
  }
  saveEntity() {
    this.$refs.stdForm.saveEntity();
  }

  setEntityAttributesAfterSave() {
    const newEntity = {} as Subscription;
    Object.keys(this.formModel).forEach(key => {
      this.$set(newEntity, key, null);
    });

    this.$set(newEntity, 'status', this.formModel.status);
    this.$set(this.$refs.stdForm, 'formModel', newEntity);
  }

  setSelectedContacts(contacts: any) {
    this.$set(this.$refs.stdForm.formModel, 'contact_id', contacts);
  }

  setSubscriptionDates(category) {
    if (category && this.modelLoaded && this.modelLoaded.subscription_category_id === category.id) {
      this.$set(this.$refs.stdForm?.formModel, 'start_date', this.modelLoaded.start_date);
      this.$set(this.$refs.stdForm?.formModel, 'end_date', this.modelLoaded.end_date);
    } else {
      let startDate: string;
      if (category['start_date_type_value'] === 'fixed_date') {
        startDate = category['fixed_start_date'];
      } else {
        startDate = moment(new Date()).format('YYYY-MM-DD');
      }

      this.$set(this.$refs.stdForm?.formModel, 'start_date', startDate);

      let endDate: string;
      switch (category['end_date_type_value']) {
        case 'fixed_date': {
          endDate = category['fixed_end_date'];
          break;
        }
        case 'fixed_duration': {
          endDate = moment(startDate)
            .add(category['fixed_duration_number'], category['duration_type_value'])
            .format('YYYY-MM-DD');
          break;
        }
        case 'without_end_date':
        default: {
          endDate = null;
          break;
        }
      }

      this.$set(this.$refs.stdForm?.formModel, 'end_date', endDate);
    }
  }

  setSubscriptionCredits(category) {
    if (this.modelLoaded && this.modelLoaded.subscription_category_id === category.id) {
      this.$set(this.$refs.stdForm?.formModel, 'total_credits', this.modelLoaded['total_credits']);
      this.$set(
        this.$refs.stdForm?.formModel,
        'remaining_credits',
        this.modelLoaded['remaining_credits'],
      );
    } else {
      this.$set(this.$refs.stdForm?.formModel, 'total_credits', category['number_credits']);
      this.$set(this.$refs.stdForm?.formModel, 'remaining_credits', category['number_credits']);
    }
  }

  private getPriceParamsStringId() {
    return [
      this.$refs.stdForm?.formModel?.start_date,
      this.$refs.stdForm?.formModel?.article,
      this.$refs.stdForm?.formModel?.price_category,
    ].join(',');
  }

  setSubscriptionPrice() {
    const oldRelatedPriceParamsStr = this.getPriceParamsStringId();
    setTimeout(() => {
      const newRelatedPriceParamsStr = this.getPriceParamsStringId();
      if (newRelatedPriceParamsStr === oldRelatedPriceParamsStr) {
        const article = this.$refs.stdForm?.formModel?.article;
        if (article) {
          const basePath = this.$bpcApiRouteHelper.getEntityPath('subscriptions');
          this.$bpcApi
            .post(basePath + '/subscription-price', {
              article: article,
              subscription: this.formModel,
            })
            .subscribe(
              res => {
                this.$set(
                  this.$refs.stdForm.formModel,
                  'subscription_price',
                  Number(res.data).toFixed(2),
                );
              },
              error => {
                console.log(error);
              },
            );
        }
      }
    }, 600);
  }

  get recreditChangeDetectorStr(): string {
    if (this.formModel?.contact_id && Array.isArray(this.formModel?.contact_id)) {
      const contacts = ((this.formModel?.contact_id as unknown) as Contact[]) || [];
      return [
        'category',
        this.formModel?.subscriptionCategory?.id,
        'contacts',
        contacts.map(contact => contact.id).join('-'),
      ].join('--');
    }
    return 'notContactSelected';
  }

  async checkIfRecreditIsPosible() {
    this.showPosibleRecreditWarning = false;
    const isRecreditCategory = !!this.formModel?.subscriptionCategory?.recredit;
    if (!isRecreditCategory) {
      return;
    }

    if (
      this.formModel.contact_id &&
      Array.isArray(this.formModel.contact_id) &&
      this.formModel.contact_id.length
    ) {
      const basePath = this.$bpcApiRouteHelper.getEntityPath('subscriptions');
      const path = basePath + '/posible-recredits';
      const res = await this.$bpcApi
        .post(path, {
          subscriptionCategoryId: this.formModel.subscriptionCategory.id,
          contactIds: (this.formModel.contact_id as Contact[]).map(contact => contact.id),
        })
        .toPromise();
      this.posibleRecredits = res.data;
      if (this.posibleRecredits.length) {
        this.showPosibleRecreditWarning = true;
      }
    }
  }

  showRecreditForm(subscriptionId) {
    console.log('showRecreditForm', subscriptionId);
    this.$emit('recredit-subscription', subscriptionId);
  }

  private modelLoaded;
  onModelLoaded(event) {
    this.modelLoaded = JSON.parse(JSON.stringify(this.$refs.stdForm?.formModel));
    if (this.entityId) {
      this.mandate = this.modelLoaded?.mandate;
    }
    this.$emit('model-loaded', event);
  }

  openAdjustCreditModal(event) {
    this.currentSubscription = this.$refs.stdForm.formModel;
    this.showAdjustCreditModal = true;
  }
  onCloseShowAdjustCreditModal() {
    this.showAdjustCreditModal = false;
  }
  onAdjustedRemainingCredits($event) {
    this.$emit('adjusted-remaining-credits');
    this.$emit('entity-saved');
  }

  @Watch('formModel', { immediate: true, deep: true })
  onFormModelChange() {
    this.$emit('input', this.formModel);
  }

  @Watch('contactChangeDetectorStr')
  onContactChangeDetectorStr() {
    // If the contact changes the mandate must be reset
    this.mandate = null;
    this.mandateKey++;
  }
  @Watch('recreditChangeDetectorStr')
  onRecreditChangeDetectorStrChange() {
    this.checkIfRecreditIsPosible();
  }
}
